import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { MessageCircle } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const FAQ = () => {
  const { t } = useLanguage();

  const faqs = [
    {
      question: "Can I see an English-speaking doctor online in Brazil?",
      answer: "Yes. Rio Med Care offers English-speaking online medical consultations throughout Brazil via secure video call. Contact us on WhatsApp to arrange an appointment."
    },
    {
      question: "How do I find an English-speaking doctor or physician near me in Brazil?",
      answer: "Wherever you are in Brazil, you can see a licensed English-speaking physician (general practitioner / family doctor) online the same day. Book on WhatsApp and join a secure video consultation from your smartphone — no waiting rooms or travel needed. Available daily 07:00-22:00."
    },
    {
      question: "Can I get a prescription in Brazil as a foreigner?",
      answer: "Yes. Our attending physicians are registered with the Brazilian Medical Council (CRM). Prescriptions and medical certificates issued when clinically indicated after a live consultation are valid throughout Brazil."
    },
    {
      question: "Do you offer in-person consultations or hotel visits?",
      answer: "No. Rio Med Care is a 100% telemedicine practice: all consultations are conducted by secure video call, in English, from anywhere in Brazil — your hotel, apartment or workplace. If your condition needs a physical examination, tests or hospital care, the physician will tell you clearly and guide you to the right service."
    },
    {
      question: "How quickly can I get an appointment?",
      answer: "Same-day appointments are often available, subject to the day's schedule. Our average reply time on WhatsApp is around 30 minutes. Online consultations are available throughout Brazil, daily 7:00-22:00."
    },
    {
      question: "Are prescriptions legally valid throughout Brazil?",
      answer: "Yes. When a prescription is clinically indicated after a live consultation, it is digitally signed and legally valid at any licensed pharmacy throughout Brazil, in accordance with CFM telemedicine regulations."
    },
    {
      question: "Do you sell or deliver medications?",
      answer: "No. Rio Med Care does not sell, dispense, or deliver any medications. We provide medical consultations and, when clinically indicated, issue digital prescriptions that you can fill at any licensed pharmacy in Brazil of your choice."
    },
    {
      question: "How is the need for a prescription decided?",
      answer: "Prescriptions are issued only after a live medical consultation (online or in-person), based on the attending physician's clinical evaluation of your symptoms, medical history, and individual circumstances. A consultation does not guarantee a prescription."
    },
    {
      question: "What payment methods do you accept?",
      answer: "We accept major credit and debit cards (Visa, Mastercard, American Express) through our secure online checkout. Payments can be made in Brazilian Reais or USD. We provide detailed receipts for insurance reimbursement requests."
    },
    {
      question: "Do you accept international health insurance?",
      answer: "We provide detailed medical receipts and documentation in English and Portuguese that most international health insurance companies accept for reimbursement. We recommend checking with your insurance provider about coverage for telemedicine and out-of-network providers."
    },
    {
      question: "Can you treat children online?",
      answer: "Yes. Rio Med Care offers online consultations for children of all ages, with parent guidance. For children under 12, we recommend having a parent or guardian present during consultations."
    },
    {
      question: "What if I need emergency medical care?",
      answer: "For life-threatening emergencies, always call 192 (Brazilian emergency services - SAMU) first. For urgent but non-life-threatening issues, contact us via WhatsApp to book an online consultation."
    },
    {
      question: "Can you provide medical certificates for travel or work?",
      answer: "Yes, we can provide medical certificates, fitness-to-travel letters, and sick leave documentation in both English and Portuguese. These are especially useful for international travelers and expatriates working in Brazil."
    },
    {
      question: "How does the online consultation work?",
      answer: "We use secure, encrypted video calling platforms compliant with medical privacy regulations. You'll receive a WhatsApp link to join the consultation. All you need is a smartphone, tablet, or computer with internet connection. Consultations are available throughout Brazil."
    }
  ];

  return (
    <section id="faq" className="py-20 bg-background">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-foreground mb-4">
            {t('faq.headline')}
          </h2>
          <p className="text-xl text-text-secondary max-w-2xl mx-auto">
            {t('faq.subheadline')}
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-card rounded-lg px-6 border"
              >
                <AccordionTrigger className="text-left font-medium hover:no-underline py-6">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="pb-6 text-text-secondary leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        <div className="text-center mt-12">
          <p className="text-text-secondary mb-4">
            Still have questions? Contact us directly!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://wa.me/5521998000676" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              WhatsApp
            </a>
            <a 
              href="mailto:riomedcare@gmail.com"
              className="inline-flex items-center justify-center px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary-hover transition-colors"
            >
              Email Us
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;